"use client";
import { useEffect, useRef } from "react";

/**
 * Cloudflare Turnstile widget — privacy-respecting CAPTCHA alternative.
 *
 * Why we use it:
 * - The contact form's existing honeypot field catches naive bots, but
 *   sophisticated form-spam tools fill hidden inputs reliably. Turnstile
 *   adds a second layer that detects automation patterns at the network
 *   and behavior level. Most real visitors get ZERO friction (no checkbox,
 *   no puzzle); only suspicious traffic gets briefly challenged.
 * - Cloudflare doesn't track visitors across sites the way Google reCAPTCHA
 *   does, which keeps this aligned with our privacy posture.
 * - It's free.
 *
 * Env-gated: if NEXT_PUBLIC_TURNSTILE_SITE_KEY is unset, this component
 * renders nothing and reports null so the contact form proceeds without
 * the extra layer. The /api/dispatch verification is symmetrically
 * env-gated on TURNSTILE_SECRET_KEY — both must be set to enforce.
 *
 * Usage:
 *   const [token, setToken] = useState<string | null>(null);
 *   <Turnstile onToken={setToken} />
 *   ...submit `token` alongside the form payload.
 */

type Props = {
  /** Called every time Turnstile produces a fresh verification token. */
  onToken: (token: string | null) => void;
  /** Optional theme override; defaults to dark to match the form chrome. */
  theme?: "light" | "dark" | "auto";
};

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          "expired-callback"?: () => void;
          "error-callback"?: () => void;
          theme?: string;
          appearance?: string;
        },
      ) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId?: string) => void;
    };
    onloadTurnstile?: () => void;
  }
}

export function Turnstile({ onToken, theme = "dark" }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);

  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

  useEffect(() => {
    if (!siteKey) return;
    if (typeof window === "undefined") return;

    // Inject the Turnstile script once. The "explicit" render mode lets us
    // control mounting per-component instead of letting the script auto-find
    // .cf-turnstile elements (which would race with our React tree).
    const SCRIPT_ID = "drr-turnstile-script";
    let script = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.src =
        "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onloadTurnstile&render=explicit";
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    const renderWidget = () => {
      if (!containerRef.current) return;
      if (widgetIdRef.current) return; // already rendered
      if (!window.turnstile) return;
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        theme,
        // appearance "interaction-only" keeps the box invisible until/unless
        // a challenge is required — best UX for legitimate visitors.
        appearance: "interaction-only",
        callback: (token: string) => onToken(token),
        "expired-callback": () => onToken(null),
        "error-callback": () => onToken(null),
      });
    };

    // If the script is already loaded (component re-mount on the same page),
    // render immediately. Otherwise wait for the onload callback.
    if (window.turnstile) {
      renderWidget();
    } else {
      window.onloadTurnstile = renderWidget;
    }

    return () => {
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // remove() throws on already-removed widgets — safe to swallow.
        }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, theme, onToken]);

  // Render nothing if Turnstile isn't configured. The form proceeds without
  // the extra layer — the existing rate limiter + honeypot still apply.
  if (!siteKey) return null;

  return <div ref={containerRef} aria-label="Bot challenge" />;
}
